import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const AlertContext = createContext();

export const AlertProvider = ({ children }) => {
  const [confirmDialog, setConfirmDialog] = useState(null);

  // Every helper below is wrapped in useCallback, and the context value in
  // useMemo, so consumers get REFERENTIALLY STABLE functions.
  //
  // Why this matters (not just a perf nicety): opening a confirm dialog sets
  // state here, which re-renders this provider. Without memoization every
  // consumer received brand-new showError/confirm identities on each of those
  // renders. Consumers that build a loader with
  // `useCallback(load, [showError])` + `useEffect(..., [load])` therefore
  // re-ran their effect and refetched from the server mid-interaction —
  // silently discarding the user's unsaved edits. That caused real data loss
  // in the PTW checklist/numbering config screens: an item removed right
  // after a confirm dialog was reinstated by the refetch before Save.
  const showSuccess = useCallback((message, duration = 3000) => {
    toast({
      variant: 'success',
      title: 'Success',
      description: message,
      duration,
    });
  }, []);

  const showError = useCallback((message, duration = 7000) => {
    toast({
      variant: 'destructive',
      title: 'Error',
      description: message,
      duration,
    });
  }, []);

  const showWarning = useCallback((message, duration = 5000) => {
    toast({
      variant: 'warning',
      title: 'Warning',
      description: message,
      duration,
    });
  }, []);

  const showInfo = useCallback((message, duration = 3000) => {
    toast({
      variant: 'info',
      title: 'Info',
      description: message,
      duration,
    });
  }, []);

  const confirm = useCallback((title, message, options = {}) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        title,
        message,
        variant: options.variant || 'default',
        confirmLabel: options.confirmLabel || 'Confirm',
        cancelLabel: options.cancelLabel || 'Cancel',
        destructive: options.destructive || false,
        onConfirm: () => {
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmDialog(null);
          resolve(false);
        },
      });
    });
  }, []);

  const value = useMemo(
    () => ({ showSuccess, showError, showWarning, showInfo, confirm }),
    [showSuccess, showError, showWarning, showInfo, confirm]
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
      {confirmDialog && (
        <AlertDialog open={true} onOpenChange={(open) => !open && confirmDialog.onCancel()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmDialog.message}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={confirmDialog.onCancel}>
                {confirmDialog.cancelLabel}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDialog.onConfirm}
                className={
                  confirmDialog.destructive
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-600'
                    : ''
                }
              >
                {confirmDialog.confirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </AlertContext.Provider>
  );
};

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};
